/* v68.36 — Referrals section for the Lead detail drawer.
   Also usable on Client detail (Q5b) — pass a `leadId` to render.

   Renders:
     - Chip list of current referrals (partner + type + status pill)
     - "Refer to…" button opening a partner picker
     - Same-type-conflict alert with "Add anyway" confirm (Q4 alert)
     - Inline status dropdown per row (open → converted etc.)
     - Optional commission % + expected fee fields on new referral
*/
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API_BASE, formatApiError } from "./AuthContext";
import {
  Share2, Plus, X, Loader2, Mail, Phone, AlertTriangle, Send, Globe,
  ExternalLink, Copy,
} from "lucide-react";
import ComposeEmailDialog from "./ComposeEmailDialog";

const STATUSES = ["open", "contacted", "quoted", "converted", "declined", "closed"];
const STATUS_COLORS = {
  open:      "bg-stone-100 text-stone-700",
  contacted: "bg-blue-50 text-blue-700",
  quoted:    "bg-amber-50 text-amber-700",
  converted: "bg-emerald-50 text-emerald-700",
  declined:  "bg-rose-50 text-rose-700",
  closed:    "bg-stone-100 text-stone-500",
};

export default function LeadReferralsSection({ leadId }) {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [types, setTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [composeCtx, setComposeCtx] = useState(null);   // v68.37 — intro-email state

  const reload = async () => {
    setLoading(true);
    try {
      const [r, p, t, ld] = await Promise.all([
        axios.get(`${API_BASE}/leads/${leadId}/referrals`),
        axios.get(`${API_BASE}/referral-partners`),
        axios.get(`${API_BASE}/referral-partner-types`),
        axios.get(`${API_BASE}/leads/${leadId}`),
      ]);
      setRows(r.data || []);
      setPartners(p.data || []);
      setTypes(t.data || []);
      setLead(ld.data || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (leadId) reload(); /* eslint-disable-next-line */ }, [leadId]);

  const updateStatus = async (assignmentId, status) => {
    try {
      await axios.patch(`${API_BASE}/referral-assignments/${assignmentId}`, { status });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (assignmentId) => {
    if (!window.confirm("Remove this referral from the lead?")) return;
    try {
      await axios.delete(`${API_BASE}/referral-assignments/${assignmentId}`);
      toast.success("Referral removed");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // v68.37/38 — Compose an intro email pre-filled with the lead summary. CC's
  // the client by default (per user ask). If the org has a saved referral
  // email template matching this partner's type (or a universal one), use it;
  // fall back to a hard-coded default otherwise.
  const openIntroEmail = async (r, ccClient) => {
    if (!r.partner?.email) {
      toast.error("This partner has no email on file");
      return;
    }
    const clientEmail = lead?.email || lead?.primary_contact_email || "";
    const partnerFirst = (r.partner.name || "").split(/\s+/)[0] || "";
    const vars = {
      lead_name:            lead?.name || "",
      lead_email:           lead?.email || lead?.primary_contact_email || "",
      lead_phone:           lead?.phone || "",
      lead_notes:           lead?.notes || "",
      lead_source:          lead?.source || "",
      partner_name:         r.partner?.name || "",
      partner_first_name:   partnerFirst,
    };
    const render = (s) => (s || "").replace(/\{\{(\w+)\}\}/g,
      (_, k) => vars[k] != null ? vars[k] : "");

    let subject = "";
    let body = "";
    try {
      const tplRes = await axios.get(
        `${API_BASE}/referral-email-templates?partner_type_id=${r.partner?.type_id || ""}`,
      );
      const tpls = tplRes.data || [];
      // Prefer a type-specific template over the universal fallback.
      const preferred = tpls.find((t) => t.partner_type_id === r.partner?.type_id)
                      || tpls[0];
      if (preferred) {
        subject = render(preferred.subject_template);
        body = render(preferred.body_template);
      }
    } catch { /* fall through to default */ }

    if (!subject || !body) {
      subject = `Referral introduction — ${lead?.name || "new client"}`;
      body = [
        `Hi ${partnerFirst || "there"},`,
        "",
        "I'd like to refer a client of ours who may benefit from your services.",
        "",
        `• Name: ${lead?.name || "—"}`,
        lead?.email ? `• Email: ${lead.email}` : null,
        lead?.phone ? `• Phone: ${lead.phone}` : null,
        lead?.source ? `• Source: ${lead.source}` : null,
        lead?.notes ? `• Notes: ${lead.notes}` : null,
        "",
        (ccClient && clientEmail)
          ? "I've CC'd them on this email so you can connect directly."
          : "Please let me know the best way to introduce you.",
        "",
        "Thanks,",
      ].filter(Boolean).join("\n");
    }

    setComposeCtx({
      to: r.partner.email,
      cc: ccClient ? clientEmail : "",
      subject, body,
      lead_id: leadId,
    });
  };

  return (
    <div data-testid="lead-referrals-section">
      <div className="flex items-center justify-between mb-2">
        <div className="overline inline-flex items-center gap-1">
          <Share2 size={11}/> Referrals ({rows.length})
        </div>
        <button
          onClick={() => setModalOpen(true)}
          disabled={partners.length === 0}
          className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="lead-referrals-add-btn"
        >
          <Plus size={11}/> Refer to…
        </button>
      </div>

      {partners.length === 0 && (
        <div className="text-[11px] text-[var(--ink-3)] italic mb-2"
             data-testid="lead-referrals-empty-directory">
          No referral partners set up yet. <Link to="/referral-partners"
             className="underline hover:text-[var(--accent)]">Add some in Settings</Link>.
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-[var(--ink-3)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)]"
             data-testid="lead-referrals-empty">
          No referrals yet.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="lead-referrals-list">
          {rows.map((r) => (
            <div key={r.assignment_id}
                 className="flex items-center gap-2 border border-[var(--border)] rounded px-2 py-1.5"
                 data-testid={`lead-referral-row-${r.assignment_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-[var(--ink)] truncate">
                  {r.partner?.name || "—"}
                  <span className="ml-1.5 text-[10px] text-[var(--ink-3)] font-normal">
                    {r.partner?.type_name || ""}
                  </span>
                  {r.idle_days >= 7 && !["converted","declined","closed"].includes(r.status) && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-0.5 rounded
                                      text-[9px] font-medium bg-amber-50 text-amber-700 align-middle"
                          title={`No update in ${r.idle_days} days — team members are nudged after 7d`}
                          data-testid={`lead-referral-idle-${r.assignment_id}`}>
                      <AlertTriangle size={8}/> {r.idle_days}d idle
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[var(--ink-3)]">
                  {r.partner?.email && (
                    <a href={`mailto:${r.partner.email}`} className="hover:text-[var(--accent)] inline-flex items-center gap-0.5">
                      <Mail size={9}/> {r.partner.email}
                    </a>
                  )}
                  {r.partner?.phone && (
                    <a href={`tel:${r.partner.phone}`} className="hover:text-[var(--accent)] inline-flex items-center gap-0.5">
                      <Phone size={9}/> {r.partner.phone}
                    </a>
                  )}
                  {r.expected_fee != null && (
                    <span>· est. ${r.expected_fee.toLocaleString()}</span>
                  )}
                </div>
              </div>
              <select value={r.status}
                      onChange={(e) => updateStatus(r.assignment_id, e.target.value)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium border-0 focus:outline-none
                                  ${STATUS_COLORS[r.status] || "bg-stone-100"}`}
                      data-testid={`lead-referral-status-${r.assignment_id}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {/* v68.37 — Intro email + open partner portal */}
              {r.partner?.email && (
                <button onClick={() => openIntroEmail(r, true)}
                        className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                        title="Email partner (CC client)"
                        data-testid={`lead-referral-email-${r.assignment_id}`}>
                  <Send size={11}/>
                </button>
              )}
              {r.partner?.portal_id_present ? null : null}
              <button onClick={() => remove(r.assignment_id)}
                      className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                      data-testid={`lead-referral-remove-${r.assignment_id}`}>
                <X size={11}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <AddReferralModal
          leadId={leadId}
          partners={partners}
          types={types}
          onClose={() => setModalOpen(false)}
          onCreated={() => { setModalOpen(false); reload(); }}
        />
      )}

      {composeCtx && (
        <ComposeEmailDialog
          open
          onClose={() => setComposeCtx(null)}
          onSent={() => { setComposeCtx(null); toast.success("Intro email sent"); }}
          context={{ lead_id: composeCtx.lead_id }}
          defaultTo={composeCtx.to}
          defaultCc={composeCtx.cc}
          defaultSubject={composeCtx.subject}
          defaultBody={composeCtx.body}
        />
      )}
    </div>
  );
}


function AddReferralModal({ leadId, partners, types, onClose, onCreated }) {
  const [partnerId, setPartnerId] = useState(partners[0]?.partner_id || "");
  const [commission, setCommission] = useState("");
  const [expectedFee, setExpectedFee] = useState("");
  const [warning, setWarning] = useState(null);  // { message, existing }
  const [busy, setBusy] = useState(false);

  const groupedByType = useMemo(() => {
    const groups = {};
    partners.forEach((p) => {
      const key = p.type_name || "Uncategorised";
      (groups[key] ||= []).push(p);
    });
    return groups;
  }, [partners]);

  const submit = async (force = false) => {
    if (!partnerId) return;
    setBusy(true);
    setWarning(null);
    try {
      const body = { partner_id: partnerId };
      if (commission !== "") body.commission_percent = parseFloat(commission);
      if (expectedFee !== "") body.expected_fee = parseFloat(expectedFee);
      const url = `${API_BASE}/leads/${leadId}/referrals${force ? "?force=true" : ""}`;
      await axios.post(url, body);
      toast.success("Referral added");
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "object"
          && detail?.code === "same_type_referral_exists") {
        setWarning(detail);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="lead-referral-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-lg p-6 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl">Refer this lead</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700">
            <X size={18} />
          </button>
        </div>
        <div>
          <label className="overline block mb-1">Partner</label>
          <select value={partnerId} onChange={(e) => { setPartnerId(e.target.value); setWarning(null); }}
                  required
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]"
                  data-testid="lead-referral-select-partner">
            {Object.entries(groupedByType).map(([type, list]) => (
              <optgroup key={type} label={type}>
                {list.map((p) => (
                  <option key={p.partner_id} value={p.partner_id}>
                    {p.name}{p.company ? ` · ${p.company}` : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="overline block mb-1">Commission %</label>
            <input type="number" min="0" max="100" step="0.1" value={commission}
                   onChange={(e) => setCommission(e.target.value)}
                   className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]"
                   data-testid="lead-referral-input-commission" />
          </div>
          <div>
            <label className="overline block mb-1">Expected fee</label>
            <input type="number" min="0" step="1" value={expectedFee}
                   onChange={(e) => setExpectedFee(e.target.value)}
                   placeholder="$" className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]"
                   data-testid="lead-referral-input-fee" />
          </div>
        </div>

        {warning && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex gap-2"
               data-testid="lead-referral-same-type-warning">
            <AlertTriangle size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-900">
              <div className="font-medium">
                A partner of this same type is already assigned:
              </div>
              <ul className="list-disc pl-4 mt-1">
                {warning.existing?.map((e) => (
                  <li key={e.assignment_id}>{e.partner_name}</li>
                ))}
              </ul>
              <div className="mt-2">Add another partner of the same type anyway?</div>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          {warning ? (
            <button onClick={() => submit(true)} disabled={busy}
                    className="btn-primary flex-1 justify-center"
                    data-testid="lead-referral-force-submit">
              {busy ? <Loader2 size={13} className="animate-spin"/> : null}
              Add anyway
            </button>
          ) : (
            <button onClick={() => submit(false)} disabled={busy || !partnerId}
                    className="btn-primary flex-1 justify-center"
                    data-testid="lead-referral-submit">
              {busy ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>}
              Refer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
